import React from 'react';
import {AbsoluteFill, Easing, interpolate, useCurrentFrame, useVideoConfig} from 'remotion';

const cream = '#f8f8f3';
const ink = '#202b23';
const green = '#315d40';
const muted = '#7d897f';
const panel = '#ffffff';

const shell = {
  backgroundColor: cream,
  color: ink,
  fontFamily: 'Arial, Helvetica, sans-serif',
  padding: 42,
};

function ScoreScene({frame, duration}) {
  const amount = interpolate(frame, [20, 120], [0, 0.78], {extrapolateRight: 'clamp', extrapolateLeft: 'clamp', easing: Easing.bezier(0.2, 0.8, 0.2, 1)});
  return <>
    <div style={{fontSize: 16, color: muted, letterSpacing: 2, textTransform: 'uppercase'}}>Funding readiness</div>
    <div style={{marginTop: 36, fontSize: 39, fontWeight: 700}}>A clear place to begin</div>
    <div style={{marginTop: 14, fontSize: 22, color: muted}}>Your answers form a practical readiness picture.</div>
    <div style={{marginTop: 72, height: 22, borderRadius: 20, background: '#e4e8df', overflow: 'hidden'}}>
      <div style={{width: `${amount * 100}%`, height: '100%', borderRadius: 20, background: green}} />
    </div>
    <div style={{display: 'flex', justifyContent: 'space-between', marginTop: 18, fontSize: 17, color: muted}}><span>Starting point</span><span>Next steps</span></div>
  </>;
}

function Rejections({frame}) {
  const labels = ['Eligibility mismatch', 'Missing attachments', 'Budget clarity', 'Fit with funder'];
  const widths = [0.68, 0.68, 0.68, 0.68];
  return <>
    <div style={{fontSize: 16, color: muted, letterSpacing: 2, textTransform: 'uppercase'}}>Grant applications</div>
    <div style={{marginTop: 20, fontSize: 36, fontWeight: 700}}>Common places to look</div>
    <div style={{marginTop: 10, fontSize: 20, color: muted}}>Review each part before you submit.</div>
    {labels.map((label, i) => {
      const progress = interpolate(frame, [18 + i * 15, 78 + i * 15], [0, widths[i]], {extrapolateRight: 'clamp', extrapolateLeft: 'clamp', easing: Easing.out(Easing.cubic)});
      const reveal = interpolate(frame, [8 + i * 14, 24 + i * 14], [0, 1], {extrapolateRight: 'clamp', extrapolateLeft: 'clamp'});
      return <div key={label} style={{marginTop: 10, opacity: reveal}}>
        <div style={{fontSize: 18, marginBottom: 9}}>{label}</div>
        <div style={{height: 12, borderRadius: 10, background: '#e4e8df'}}><div style={{width: `${progress * 100}%`, height: 12, borderRadius: 10, background: green}} /></div>
      </div>;
    })}
  </>;
}

function Checklist({frame}) {
  const labels = ['501(c)(3) determination letter', 'Form 990', 'Financial statements', 'Board list', 'Project budget'];
  return <>
    <div style={{fontSize: 16, color: muted, letterSpacing: 2, textTransform: 'uppercase'}}>Before the proposal</div>
    <div style={{marginTop: 20, fontSize: 36, fontWeight: 700}}>Gather the essentials</div>
    {labels.map((label, i) => {
      const done = interpolate(frame, [18 + i * 24, 32 + i * 24], [0, 1], {extrapolateRight: 'clamp', extrapolateLeft: 'clamp'});
      return <div key={label} style={{display: 'flex', alignItems: 'center', gap: 18, marginTop: 21, fontSize: 20, opacity: 0.55 + 0.45 * done}}>
        <div style={{width: 29, height: 29, borderRadius: 8, background: done ? green : panel, border: `2px solid ${done ? green : '#aeb7ae'}`, color: whiteColor(done), display: 'grid', placeItems: 'center', fontWeight: 700}}>{done > 0.9 ? '✓' : ''}</div>{label}
      </div>;
    })}
  </>;
}

const whiteColor = (done) => done > 0.9 ? '#fff' : ink;

function Assurance({frame}) {
  const cards = [
    ['Audit', 'Highest assurance', 'When required by a funder or board policy'],
    ['Review', 'Limited assurance', 'When a funder asks for CPA review'],
    ['Compilation', 'No assurance', 'When organized statements are needed'],
  ];
  return <>
    <div style={{fontSize: 16, color: muted, letterSpacing: 2, textTransform: 'uppercase'}}>Financial statements</div>
    <div style={{marginTop: 18, fontSize: 35, fontWeight: 700}}>Three levels of CPA work</div>
    <div style={{display: 'flex', gap: 16, marginTop: 43}}>{cards.map(([title, level, note], i) => {
      const x = interpolate(frame, [16 + i * 14, 52 + i * 14], [38, 0], {extrapolateRight: 'clamp', extrapolateLeft: 'clamp', easing: Easing.out(Easing.cubic)});
      const opacity = interpolate(frame, [16 + i * 14, 48 + i * 14], [0, 1], {extrapolateRight: 'clamp', extrapolateLeft: 'clamp'});
      return <div key={title} style={{flex: 1, height: 180, background: panel, borderRadius: 18, padding: 19, boxSizing: 'border-box', borderTop: `5px solid ${green}`, transform: `translateY(${x}px)`, opacity}}>
        <div style={{fontSize: 25, fontWeight: 700}}>{title}</div><div style={{marginTop: 16, color: green, fontSize: 16, fontWeight: 700}}>{level}</div><div style={{marginTop: 15, fontSize: 16, lineHeight: 1.4, color: muted}}>{note}</div>
      </div>;
    })}</div>
    <div style={{position: 'absolute', bottom: 42, left: 42, right: 42, fontSize: 16, color: muted}}>Ask the funder which form they accept.</div>
  </>;
}

function Board({frame}) {
  const seats = Array.from({length: 8}, (_, i) => i);
  return <>
    <div style={{fontSize: 16, color: muted, letterSpacing: 2, textTransform: 'uppercase'}}>Board readiness</div>
    <div style={{marginTop: 20, fontSize: 36, fontWeight: 700}}>Make room for shared leadership</div>
    <div style={{position: 'absolute', left: 184, top: 198, width: 352, height: 150, borderRadius: 84, background: '#e3e8df', border: `3px solid ${green}`}} />
    {seats.map((seat, i) => {
      const angle = (Math.PI * 2 * i) / seats.length - Math.PI / 2;
      const cx = 360 + Math.cos(angle) * 224;
      const cy = 276 + Math.sin(angle) * 103;
      const fill = interpolate(frame, [18 + i * 18, 34 + i * 18], [0, 1], {extrapolateRight: 'clamp', extrapolateLeft: 'clamp'});
      return <div key={seat} style={{position: 'absolute', left: cx - 19, top: cy - 19, width: 38, height: 38, borderRadius: 12, background: fill ? green : '#d9ded8', border: `2px solid ${green}`, transform: `scale(${0.84 + fill * 0.16})`}} />;
    })}
    <div style={{position: 'absolute', left: 322, top: 224, width: 76, height: 52, borderRadius: 14, background: cream, border: `2px dashed ${green}`, display: 'grid', placeItems: 'center', color: green, fontSize: 20, fontWeight: 700}}>5+</div>
  </>;
}

function Timeline({frame}) {
  const steps = ['Register', 'Build governance', 'Track finances', 'Choose a funder', 'Apply', 'First award'];
  const progress = interpolate(frame, [20, 260], [0, 1], {extrapolateRight: 'clamp', extrapolateLeft: 'clamp', easing: Easing.bezier(0.2, 0.8, 0.2, 1)});
  return <>
    <div style={{fontSize: 16, color: muted, letterSpacing: 2, textTransform: 'uppercase'}}>A first grant takes preparation</div>
    <div style={{marginTop: 20, fontSize: 36, fontWeight: 700}}>Build one step at a time</div>
    <div style={{position: 'absolute', left: 74, right: 74, top: 226, height: 7, background: '#dfe5dc', borderRadius: 8}}><div style={{width: `${progress * 100}%`, height: 7, background: green, borderRadius: 8}} /></div>
    <div style={{position: 'absolute', left: 74, right: 74, top: 200, display: 'flex', justifyContent: 'space-between'}}>{steps.map((step, i) => {
      const point = i / (steps.length - 1);
      const active = progress >= point;
      return <div key={step} style={{width: 94, textAlign: 'center'}}>
        <div style={{margin: '0 auto 18px', width: 23, height: 23, borderRadius: '50%', background: active ? green : '#dfe5dc', border: `3px solid ${cream}`, boxShadow: `0 0 0 2px ${active ? green : '#aeb7ae'}`}} />
        <div style={{fontSize: 16, fontWeight: i === steps.length - 1 ? 700 : 400, color: active ? ink : muted}}>{step}</div>
      </div>;
    })}</div>
    <div style={{position: 'absolute', left: 42, right: 42, bottom: 37, textAlign: 'center', color: muted, fontSize: 18}}>Your next step depends on your organization and the funder.</div>
  </>;
}

export const PeaklamenClip = ({scene}) => {
  const frame = useCurrentFrame();
  const {durationInFrames} = useVideoConfig();
  const components = {score: ScoreScene, rejections: Rejections, checklist: Checklist, assurance: Assurance, board: Board, timeline: Timeline};
  const Component = components[scene] || ScoreScene;
  return <AbsoluteFill style={{backgroundColor: cream, opacity: interpolate(frame, [0, 12, durationInFrames - 20, durationInFrames - 1], [0, 1, 1, 0], {extrapolateRight: 'clamp', extrapolateLeft: 'clamp'})}}><div style={{...shell, width: 720, height: 405, transform: 'scale(1.3333333)', transformOrigin: 'top left', boxSizing: 'border-box'}}><Component frame={frame} duration={durationInFrames} /></div></AbsoluteFill>;
};
